import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import HudBackground from '../components/hud/HudBackground';
import UserManagement from '../components/admin/UserManagement';
import NewsManagement from '../components/admin/NewsManagement';
import OrderManagement from '../components/admin/OrderManagement';
import OperationManagement from '../components/admin/OperationManagement';
import DeviceCatalogManager from '../components/admin/DeviceCatalogManager';
import FieldMissionDeviceManager from '../components/admin/FieldMissionDeviceManager';
import OperationRecordManager from '../components/admin/OperationRecordManager';
import OperationFieldConfigManager from '../components/admin/OperationFieldConfigManager';
import FieldNodeManager from '../components/admin/FieldNodeManager';
import AchievementManagement from '../components/admin/AchievementManagement';
import RanksManagement from '../components/admin/RanksManagement';
import SubscriptionManagement from '../components/admin/SubscriptionManagement';
import { performLogout } from '../services/authService';
import axios from 'axios';
import { API_URL } from '../constants/api';
import {
  AdminContainer,
  AdminPageTitle,
  AdminStatusBar,
  AdminMainContent,
  AdminSidebar,
  AdminControlPanel,
  AdminControlPanelHeader,
  AdminControlPanelNav,
  AdminContentArea,
  AdminNavItem,
  AdminNavGroup,
  AdminNavGroupHeader,
  AdminNavSubMenu,
  AdminNavSubItem,
  AdminInfoBox,
  AdminSystemStatus,
  AdminStatusItem,
  AdminCard,
  AdminCardHeader,
  AdminMetricsGrid,
  AdminMetricCard,
  AdminMetricLabel,
  AdminMetricValue,
  AdminMetricFooter,
  AdminFooter,
  AdminLogoutButton,
  adminColors,
} from '../styles/adminStyles';

// Stats Grid for dashboard
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px dashed ${adminColors.borderMuted};
  font-family: 'Courier New', monospace;
  font-size: 13px;

  span:first-child {
    color: ${adminColors.secondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  span:last-child {
    color: ${adminColors.text};
    font-weight: 600;
  }

  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
`;

const AdminPage: React.FC = () => {
  const [stats] = useState({
    totalUsers: 1245,
    userGrowth: '+12% THIS MONTH',
    activeMissions: 78,
    deploymentRate: '92%',
    totalRevenue: 457890,
    revenueToday: 12450,
    revenueMonth: 87320,
    averageOrder: 3250,
    popularMission: 'GHOST PROTOCOL',
    growthRate: '+15.4%'
  });

  const [systemStatus] = useState({
    apiServer: 'ONLINE',
    database: 'OPTIMAL',
    memory: '64%',
    uptime: '18d 3h 27m'
  });

  const [activeSection, setActiveSection] = useState<string>('dashboard');
  const [operationsExpanded, setOperationsExpanded] = useState(false);
  const [devicesExpanded, setDevicesExpanded] = useState(false);

  const operationsSubSections = ['productOperations', 'operationRecords', 'fieldConfigs', 'fieldNodes'];
  const isOperationsActive = operationsSubSections.includes(activeSection);

  const devicesSubSections = ['deviceCatalog', 'fieldMissionDevices'];
  const isDevicesActive = devicesSubSections.includes(activeSection);

  const sessionId = `SES-${Math.floor(Math.random() * 1000000)}`;

  const getCurrentDate = () => {
    const date = new Date();
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    performLogout();
    navigate('/login');
  };

  useEffect(() => {
    const checkTokenValidity = async () => {
      try {
        await axios.get(`${API_URL}/api/admin/users?page=1&pageSize=1`);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          console.log('Admin token invalid, forcing logout');
          performLogout();
          window.location.href = '/';
        }
      }
    };

    checkTokenValidity();
    const interval = setInterval(checkTokenValidity, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const renderDashboard = () => (
    <>
      <AdminCard>
        <AdminCardHeader>SYSTEM METRICS</AdminCardHeader>
        <AdminMetricsGrid>
          <AdminMetricCard>
            <AdminMetricLabel>TOTAL USERS</AdminMetricLabel>
            <AdminMetricValue>{stats.totalUsers.toLocaleString()}</AdminMetricValue>
            <AdminMetricFooter>{stats.userGrowth}</AdminMetricFooter>
          </AdminMetricCard>
          <AdminMetricCard>
            <AdminMetricLabel>ACTIVE MISSIONS</AdminMetricLabel>
            <AdminMetricValue>{stats.activeMissions}</AdminMetricValue>
            <AdminMetricFooter>DEPLOYMENT RATE: {stats.deploymentRate}</AdminMetricFooter>
          </AdminMetricCard>
        </AdminMetricsGrid>
      </AdminCard>

      <AdminCard>
        <AdminCardHeader>REVENUE STATISTICS</AdminCardHeader>
        <StatsGrid>
          <div>
            <StatItem>
              <span>TOTAL REVENUE:</span>
              <span>{stats.totalRevenue.toLocaleString()} RUB</span>
            </StatItem>
            <StatItem>
              <span>REVENUE TODAY:</span>
              <span>{stats.revenueToday.toLocaleString()} RUB</span>
            </StatItem>
            <StatItem>
              <span>REVENUE THIS MONTH:</span>
              <span>{stats.revenueMonth.toLocaleString()} RUB</span>
            </StatItem>
          </div>
          <div>
            <StatItem>
              <span>AVERAGE ORDER VALUE:</span>
              <span>{stats.averageOrder.toLocaleString()} RUB</span>
            </StatItem>
            <StatItem>
              <span>MOST POPULAR MISSION:</span>
              <span>{stats.popularMission}</span>
            </StatItem>
            <StatItem>
              <span>GROWTH RATE:</span>
              <span>{stats.growthRate}</span>
            </StatItem>
          </div>
        </StatsGrid>
      </AdminCard>
    </>
  );

  return (
    <HudBackground>
      <AdminContainer>
        <AdminPageTitle>COMMAND CENTRAL</AdminPageTitle>
        <AdminStatusBar>
          ADMIN ACCESS LEVEL: ALPHA | ENCRYPTION: ACTIVE | SESSION ID: {sessionId}
          <AdminLogoutButton onClick={handleLogout}>LOGOUT</AdminLogoutButton>
        </AdminStatusBar>

        <AdminMainContent>
          <AdminSidebar>
            <AdminControlPanel>
              <AdminControlPanelHeader>CONTROL PANEL</AdminControlPanelHeader>
              <AdminControlPanelNav>
                <AdminNavItem to="#" $active={activeSection === 'dashboard'} onClick={() => setActiveSection('dashboard')}>DASHBOARD</AdminNavItem>

                <AdminNavGroup>
                  <AdminNavGroupHeader
                    $active={isDevicesActive}
                    $expanded={devicesExpanded}
                    onClick={() => setDevicesExpanded(!devicesExpanded)}
                  >
                    DEVICES
                  </AdminNavGroupHeader>
                  <AdminNavSubMenu $expanded={devicesExpanded}>
                    <AdminNavSubItem to="#" $active={activeSection === 'deviceCatalog'} onClick={() => setActiveSection('deviceCatalog')}>Device Catalog</AdminNavSubItem>
                    <AdminNavSubItem to="#" $active={activeSection === 'fieldMissionDevices'} onClick={() => setActiveSection('fieldMissionDevices')}>Field Mission Devices</AdminNavSubItem>
                  </AdminNavSubMenu>
                </AdminNavGroup>

                <AdminNavGroup>
                  <AdminNavGroupHeader
                    $active={isOperationsActive}
                    $expanded={operationsExpanded}
                    onClick={() => setOperationsExpanded(!operationsExpanded)}
                  >
                    OPERATIONS
                  </AdminNavGroupHeader>
                  <AdminNavSubMenu $expanded={operationsExpanded}>
                    <AdminNavSubItem to="#" $active={activeSection === 'productOperations'} onClick={() => setActiveSection('productOperations')}>Product Operations</AdminNavSubItem>
                    <AdminNavSubItem to="#" $active={activeSection === 'operationRecords'} onClick={() => setActiveSection('operationRecords')}>Operation Records</AdminNavSubItem>
                    <AdminNavSubItem to="#" $active={activeSection === 'fieldConfigs'} onClick={() => setActiveSection('fieldConfigs')}>Field Configs</AdminNavSubItem>
                    <AdminNavSubItem to="#" $active={activeSection === 'fieldNodes'} onClick={() => setActiveSection('fieldNodes')}>Field Nodes</AdminNavSubItem>
                  </AdminNavSubMenu>
                </AdminNavGroup>

                <AdminNavItem to="#" $active={activeSection === 'subscriptions'} onClick={() => setActiveSection('subscriptions')}>SUBSCRIPTIONS</AdminNavItem>
                <AdminNavItem to="#" $active={activeSection === 'users'} onClick={() => setActiveSection('users')}>USERS</AdminNavItem>
                <AdminNavItem to="#" $active={activeSection === 'achievements'} onClick={() => setActiveSection('achievements')}>ACHIEVEMENTS</AdminNavItem>
                <AdminNavItem to="#" $active={activeSection === 'ranks'} onClick={() => setActiveSection('ranks')}>RANKS</AdminNavItem>
                <AdminNavItem to="#" $active={activeSection === 'news'} onClick={() => setActiveSection('news')}>NEWS</AdminNavItem>
                <AdminNavItem to="#" $active={activeSection === 'comments'} onClick={() => setActiveSection('comments')}>COMMENTS</AdminNavItem>
                <AdminNavItem to="#" $active={activeSection === 'orders'} onClick={() => setActiveSection('orders')}>ORDERS</AdminNavItem>
              </AdminControlPanelNav>
            </AdminControlPanel>

            <AdminInfoBox>
              ADMIN: COMMANDER ALPHA<br />
              LAST LOGIN: {getCurrentDate()}<br />
              SECURITY: MAXIMUM
            </AdminInfoBox>

            <AdminSystemStatus>
              <h3>SYSTEM STATUS</h3>
              <AdminStatusItem>
                <span>API SERVER:</span>
                <span>{systemStatus.apiServer}</span>
              </AdminStatusItem>
              <AdminStatusItem>
                <span>DATABASE:</span>
                <span>{systemStatus.database}</span>
              </AdminStatusItem>
              <AdminStatusItem>
                <span>MEMORY:</span>
                <span>{systemStatus.memory}</span>
              </AdminStatusItem>
              <AdminStatusItem>
                <span>UPTIME:</span>
                <span>{systemStatus.uptime}</span>
              </AdminStatusItem>
            </AdminSystemStatus>
          </AdminSidebar>

          <AdminContentArea>
            {activeSection === 'dashboard' && renderDashboard()}
            {activeSection === 'productOperations' && <OperationManagement />}
            {activeSection === 'deviceCatalog' && <DeviceCatalogManager />}
            {activeSection === 'fieldMissionDevices' && <FieldMissionDeviceManager />}
            {activeSection === 'operationRecords' && <OperationRecordManager />}
            {activeSection === 'fieldConfigs' && <OperationFieldConfigManager />}
            {activeSection === 'fieldNodes' && <FieldNodeManager />}
            {activeSection === 'users' && <UserManagement />}
            {activeSection === 'achievements' && <AchievementManagement />}
            {activeSection === 'ranks' && <RanksManagement />}
            {activeSection === 'news' && <NewsManagement />}
            {activeSection === 'orders' && <OrderManagement />}
            {activeSection === 'subscriptions' && <SubscriptionManagement />}
          </AdminContentArea>
        </AdminMainContent>

        <AdminFooter>
          2025 NOVAWARFARE | ADMIN CONSOLE v2.3.1 | ACCESS LEVEL: ALPHA CLEARANCE
        </AdminFooter>
      </AdminContainer>
    </HudBackground>
  );
};

export default AdminPage;
